import {
  Box,
  Divider,
  IconButton,
  SlidersHIcon,
  Theme,
  Tooltip,
  Typography,
  makeStyles,
  useModal,
  useTheme,
} from '@forgeglobal/design-framework'
import { ColumnSortProps, ExpansionTable } from 'isolated-design-library/components/expansion-table/ExpansionTable'
import { IssuerFilter } from 'components/order-book/IssuerFilter'
import {
  OrderBookCompanyFragment,
  OrderBookFilterInput,
  SortDirection,
  useOrderBookQuery,
} from 'graphql/bindings/AdminBindings'
import { OrderBookRow } from 'components/order-book/OrderBookRow'
import React, { useState } from 'react'

export const ColumnDefs = [
  { name: 'issuer', text: 'Issuer Name', width: 3 },
  { name: 'valuation', text: 'Valuation', width: 1, sortable: true },
  { name: 'bid_volume', text: 'Bid Vol', width: 1, sortable: true },
  { name: 'bid_volume_actionable', text: '$ Actionable', width: 1, sortable: true },
  { name: 'bid_structures', text: 'Bid Structures', width: 2 },
  { name: 'ask_volume', text: 'Ask Vol', width: 1, sortable: true },
  { name: 'ask_volume_actionable', text: '$ Actionable', width: 1, sortable: true },
  { name: 'ask_structures', text: 'Ask Structures', width: 2 },
]

const useStyles = makeStyles((theme: Theme) => ({
  backgroundShaded: {
    backgroundColor: theme.palette.background.default,
    height: '100%',
  },
  headingContainer: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  buttonAdjust: {
    marginLeft: theme.spacing(-1),
    marginTop: theme.spacing(-0.5),
  },
  nudgeUp: {
    marginTop: theme.spacing(-0.25),
  },
  iconDivider: {
    backgroundColor: '#e0e0e0',
    width: '1px',
    height: theme.spacing(4),
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
  contentArea: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
}))

export const OrderBook: React.FC = () => {
  const theme = useTheme()
  const displayIfNone = <Typography variant="caption">Loading ...</Typography>

  const [sortState, setSortState] = useState<ColumnSortProps>({
    field: 'company_rank',
    direction: SortDirection.Descending,
  })
  const [filterState, setFilterState] = useState<OrderBookFilterInput>({})

  const onClickSort = (columnName: string) => {
    const column = ColumnDefs.find(col => col.name === columnName)
    if (!column || !column.sortable) return

    if (columnName !== sortState.field) {
      setSortState({ field: columnName, direction: SortDirection.Descending })
      return
    }
    switch (sortState.direction) {
      case SortDirection.Descending:
        setSortState({ field: columnName, direction: SortDirection.Ascending })
        return
      case SortDirection.Ascending:
        setSortState({ field: 'company_rank', direction: SortDirection.Descending })
        return
    }
  }

  const { data, loading, fetchMore } = useOrderBookQuery({
    variables: {
      sort: {
        field: sortState.field,
        direction:
          sortState.direction === SortDirection.Descending ? SortDirection.Descending : SortDirection.Ascending,
      },
      filter: filterState,
    },
  })

  const rowData = data && !loading ? data.orderBookCompanies.data : Array<OrderBookCompanyFragment>()
  const count = data && !loading ? data.orderBookCompanies.count : 0

  const loadMore = (fromIndex: number, toIndex: number) => {
    if (loading || toIndex < rowData.length) {
      return Promise.resolve()
    }

    return fetchMore({
      variables: {
        offset: rowData.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return {
          orderBookCompanies: Object.assign({}, prev.orderBookCompanies, {
            data: prev.orderBookCompanies.data.slice(0, rowData.length).concat(fetchMoreResult.orderBookCompanies.data),
          }),
        }
      },
    }).then(() => Promise.resolve())
  }

  const classes = useStyles()

  const applyFilter = (filter: OrderBookFilterInput) => {
    setFilterState(filter)
    hideFiltersModal()
  }

  const [showFiltersModal, hideFiltersModal] = useModal(() => {
    return <IssuerFilter filter={filterState} applyFilter={applyFilter} onClose={hideFiltersModal} />
  }, [filterState])

  const numFilters =
    (filterState?.askVolumeActive?.length ?? 0) +
    (filterState?.bidVolumeActive?.length ?? 0) +
    (filterState?.tradeStructuresActive?.length ?? 0) +
    (filterState?.valuation?.length ?? 0)

  return (
    <Box m={0} p={0} className={classes.backgroundShaded}>
      <Box px={3} py={2} className={classes.contentArea}>
        <Box paddingX={3} paddingTop={1} paddingBottom={2} className={classes.headingContainer}>
          <Tooltip title="Filter Issuers" placement="top">
            <IconButton size="medium" onClick={showFiltersModal} className={classes.buttonAdjust}>
              <SlidersHIcon />
            </IconButton>
          </Tooltip>
          {numFilters > 0 && (
            <Tooltip title="Number of applied filters" placement="top">
              <Typography color="secondary" variant="body2" className={classes.nudgeUp}>
                {`(${numFilters})`}
              </Typography>
            </Tooltip>
          )}
          <span className={classes.iconDivider} />
          <Typography color="secondary" variant="h6" style={{ textTransform: 'none' }}>
            Issuers
          </Typography>
          <div style={{ flexGrow: 1 }} />
          <Typography color="textSecondary" variant="subtitle2">
            {count && `${count} Total`}
          </Typography>
        </Box>
        <Divider />
        <ExpansionTable
          columnDefs={ColumnDefs}
          sort={sortState}
          onClickSort={onClickSort}
          rowHeights={{ collapsed: theme.spacing(8), expanded: theme.spacing(70) }}
          data={rowData}
          loadMore={loadMore}
          component={OrderBookRow}
          displayIfNone={displayIfNone}
          rowCount={count}
        />
      </Box>
    </Box>
  )
}
